<template>
  <div class="cap-heatmap-wrap">
    <!-- Loading state -->
    <div v-if="loading" class="cap-loading" :style="{ height: maxHeight }">
      Loading…
    </div>

    <!-- Grid -->
    <div
      v-else
      class="cap-grid-scroll"
      :style="{
        minHeight: maxHeight,
        maxHeight: maxHeight,
        overflowY: maxHeight ? 'auto' : 'visible',
      }"
    >
      <div class="cap-grid" :style="gridTemplateStyle" @click.self="clearDrill">
        <!-- Corner cell -->
        <div class="cap-corner cap-sticky-tl">Process</div>

        <!-- Column headers -->
        <div
          v-for="col in columns"
          :key="col.key"
          class="cap-col-hdr cap-sticky-top"
          :class="{ 'cap-col-hl': highlightCol === col.key }"
          @click="toggleColHighlight(col.key)"
        >
          <span v-if="hasOverCapacity(col.key)" class="cap-over-dot"></span>
          <div class="cap-col-label">{{ col.label }}</div>
          <div class="cap-col-sub">{{ col.subLabel }}</div>
        </div>

        <!-- Rows -->
        <template v-for="proc in visibleProcesses">
          <!-- Row header -->
          <div
            :key="'rh-' + proc.ProcessCode"
            class="cap-row-hdr cap-sticky-left"
            :class="{ 'cap-row-hl': highlightRow === proc.ProcessCode }"
            @click="toggleRowHighlight(proc.ProcessCode)"
          >
            <div class="cap-proc-name">
              {{ proc.Description }}
            </div>
            <div
              class="cap-avg"
              :class="avgTierClass(rowAverages[proc.ProcessCode])"
            >
              {{
                rowAverages[proc.ProcessCode] !== null
                  ? `Avg ${rowAverages[proc.ProcessCode]}%`
                  : "—"
              }}
            </div>
          </div>

          <!-- Cells -->
          <div
            v-for="col in columns"
            :key="proc.ProcessCode + '-' + col.key"
            class="cap-cell"
            :class="[
              cellTierClass(getCellData(proc.ProcessCode, col.key)),
              { 'cap-col-hl': highlightCol === col.key },
              { 'cap-row-hl': highlightRow === proc.ProcessCode },
              {
                'cap-cell-active':
                  drillCell &&
                  drillCell.processCode === proc.ProcessCode &&
                  drillCell.colKey === col.key,
              },
              'hm-cell',
            ]"
            @click="openDrill(proc, col)"
            @mouseenter="showTooltip($event, proc, col)"
            @mouseleave="hideTooltip"
          >
            <template v-if="getCellData(proc.ProcessCode, col.key)">
              <div class="cap-pct">
                {{ getCellData(proc.ProcessCode, col.key).pct }}%
              </div>
              <div class="cap-hrs">
                {{
                  getCellData(proc.ProcessCode, col.key).demandHours.toFixed(2)
                }}h
              </div>
            </template>
            <template v-else>
              <div class="cap-nodata">—</div>
            </template>
          </div>
        </template>
      </div>
    </div>

    <!-- Drill panel -->
    <CapacityDrillPanel
      v-if="drillCell"
      :cell="drillCell"
      :current-view="currentView"
      @close="clearDrill"
      @view-in-schedule="onViewInSchedule"
    />

    <!-- Tooltip -->
    <CapacityTooltip
      v-if="tooltip.visible"
      :content="tooltip.content"
      :x="tooltip.x"
      :y="tooltip.y"
    />

    <!-- Legend -->
    <div class="cap-legend">
      <div class="cap-legend-items">
        <span class="leg-item">
          <span
            class="leg-swatch"
            style="background: #f9fafb; border: 1px solid #e5e7eb"
          ></span
          >No data
        </span>
        <span class="leg-item">
          <span class="leg-swatch" style="background: #f0fdf4"></span>&lt;60%
        </span>
        <span class="leg-item">
          <span class="leg-swatch" style="background: #dcfce7"></span>60–79%
        </span>
        <span class="leg-item">
          <span class="leg-swatch" style="background: #fef3c7"></span>80–89%
        </span>
        <span class="leg-item">
          <span class="leg-swatch" style="background: #fee2e2"></span>90–99%
        </span>
        <span class="leg-item">
          <span class="leg-swatch" style="background: #fecaca"></span>100%+
        </span>
      </div>
      <div class="cap-legend-right">
        <span class="leg-over-dot"></span>
        <span class="leg-over-label">Over-capacity</span>
        <span class="leg-hint">Click any cell to drill down</span>
      </div>
    </div>
  </div>
</template>

<script>
import { CapacityHeatmapService } from "@/_services/api/production/capacity-heatmap.service";
import CapacityDrillPanel from "./CapacityDrillPanel.vue";
import CapacityTooltip from "./CapacityTooltip.vue";

export default {
  name: "CapacityHeatmap",
  components: {
    CapacityDrillPanel,
    CapacityTooltip,
  },

  props: {
    branchCode: { type: String, required: true },
    currentDate: { type: Date, required: true },
    currentView: { type: String, required: true },
    processes: { type: Array, default: () => [] },
    showLoading: { type: Function, default: () => {} },
    maxHeight: { type: String, default: null },
  },

  emits: ["navigate-to-schedule"],

  data() {
    return {
      test: false,
      utilisationGrid: {},
      loading: false,
      highlightCol: null,
      highlightRow: null,
      drillCell: null,
      tooltip: { visible: false, content: null, x: 0, y: 0 },
      viewMode: "detailed",
      summaryProcessList: [],
    };
  },

  computed: {
    // Transform processes from tab format (code/text) to API format (ProcessCode/Description)
    normalizedProcesses() {
      return this.processes.map((proc) => ({
        ProcessCode: proc.code,
        Description: proc.text,
      }));
    },

    // Filter processes based on viewMode and SummaryProcessList
    // In Summary mode, sort by the order defined in SummaryProcessList
    visibleProcesses() {
      if (this.viewMode === "detailed") {
        return this.normalizedProcesses;
      }
      // Summary mode: filter by SummaryProcessList and sort by its order
      if (this.summaryProcessList.length === 0) {
        return [];
      }
      return this.normalizedProcesses
        .filter((proc) => this.summaryProcessList.includes(proc.ProcessCode))
        .sort((a, b) =>
          this.summaryProcessList.indexOf(a.ProcessCode) -
          this.summaryProcessList.indexOf(b.ProcessCode),
        );
    },

    columns() {
      const d = this.currentDate;
      const MONTHS = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

      const toStr = (dt) =>
        `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(
          2,
          "0",
        )}-${String(dt.getDate()).padStart(2, "0")}`;

      const mondayOf = (dt) => {
        const r = new Date(dt);
        const day = r.getDay();
        r.setDate(r.getDate() - (day === 0 ? 6 : day - 1));
        return r;
      };

      const sundayOf = (dt) => {
        const r = new Date(dt);
        r.setDate(r.getDate() - r.getDay());
        return r;
      };

      if (this.currentView === "TimelineDay") {
        // 1 column: the selected date only
        return [
          {
            key: toStr(d),
            label: `${DAYS[d.getDay()]} ${d.getDate()} ${MONTHS[d.getMonth()]}`,
            subLabel: MONTHS[d.getMonth()],
            dates: [toStr(d)],
          },
        ];
      }

      if (this.currentView === "TimelineWorkWeek") {
        // 5 columns: Mon–Fri of the work week containing currentDate
        const mon = mondayOf(d);
        return [0, 1, 2, 3, 4].map((offset) => {
          const dt = new Date(mon);
          dt.setDate(mon.getDate() + offset);
          return {
            key: toStr(dt),
            label: `${DAYS[dt.getDay()]} ${dt.getDate()} ${
              MONTHS[dt.getMonth()]
            }`,
            subLabel: MONTHS[dt.getMonth()],
            dates: [toStr(dt)],
          };
        });
      }

      if (this.currentView === "TimelineWeek") {
        // 7 columns: Sun–Sat of the week containing currentDate
        const sun = sundayOf(d);
        return [0, 1, 2, 3, 4, 5, 6].map((offset) => {
          const dt = new Date(sun);
          dt.setDate(sun.getDate() + offset);
          return {
            key: toStr(dt),
            label: `${DAYS[dt.getDay()]} ${dt.getDate()} ${
              MONTHS[dt.getMonth()]
            }`,
            subLabel: MONTHS[dt.getMonth()],
            dates: [toStr(dt)],
          };
        });
      }

      if (this.currentView === "TimelineMonth") {
        // 5 columns: the month containing currentDate ±2 months
        return [-2, -1, 0, 1, 2].map((offset) => {
          const dt = new Date(d.getFullYear(), d.getMonth() + offset, 1);
          const year = dt.getFullYear();
          const month = dt.getMonth();
          const daysInMonth = new Date(year, month + 1, 0).getDate();
          const dates = Array.from({ length: daysInMonth }, (_, i) => {
            const day = new Date(year, month, i + 1);
            return toStr(day);
          });
          return {
            key: `${year}-${String(month + 1).padStart(2, "0")}`,
            label: MONTHS[month],
            subLabel: String(year),
            dates,
          };
        });
      }

      return [];
    },

    gridTemplateStyle() {
      return {
        gridTemplateColumns: `160px repeat(${this.columns.length}, minmax(100px, 1fr))`,
      };
    },

    rowAverages() {
      const avgs = {};
      this.normalizedProcesses.forEach((proc) => {
        const values = this.columns
          .map(
            (col) =>
              this.utilisationGrid[proc.ProcessCode] &&
              this.utilisationGrid[proc.ProcessCode][col.key],
          )
          .filter((cell) => cell !== null && cell !== undefined)
          .map((cell) => cell.pct);
        avgs[proc.ProcessCode] =
          values.length === 0
            ? null
            : Math.round(values.reduce((s, v) => s + v, 0) / values.length);
      });
      return avgs;
    },
  },

  watch: {
    branchCode() {
      this.highlightCol = null;
      this.highlightRow = null;
      this.clearDrill();
      this.fetchData();
    },
    currentView() {
      this.highlightCol = null;
      this.highlightRow = null;
      this.clearDrill();
      this.fetchData();
    },
    currentDate() {
      this.clearDrill();
      this.fetchData();
    },
  },

  async mounted() {
    await this.loadFeatureSettings();
    this.fetchData();
  },

  methods: {
    /**
     * Load feature settings for CapacityHeatmap (summary process list, export attribute, export value)
     * Settings are maintained by Graycorp Delivery/Support; no customer-facing edit UI
     * @returns {Promise<void>}
     */
    async loadFeatureSettings() {
      try {
        const settings = await CapacityHeatmapService.getFeatureSettings();
        if (settings.SummaryProcessList) {
          this.summaryProcessList = settings.SummaryProcessList.split(",")
            .map((code) => code.trim())
            .filter((code) => code.length > 0);
        }
      } catch (err) {
        // Error loading settings — summaryProcessList remains empty
      } finally {
        // No further action needed
      }
    },

    /**
     * Toggle view mode between 'detailed' and 'summary'
     * Clears grid highlights and closes drill panel
     * @param {string} mode - 'detailed' or 'summary'
     */
    toggleViewMode(mode) {
      this.viewMode = mode;
      this.highlightCol = null;
      this.highlightRow = null;
      this.clearDrill();
    },

    async fetchData() {
      if (!this.branchCode || this.columns.length === 0) {
        return;
      }

      this.showLoading(true);
      this.loading = true;

      try {
        // Date range spans all dates across all columns
        const allDates = this.columns.flatMap((c) => c.dates).sort();
        const startDate = allDates[0];
        const endDate = allDates[allDates.length - 1];

        const [capacityList, demandList] = await Promise.all([
          CapacityHeatmapService.getCapacityData(
            this.branchCode,
            startDate,
            endDate,
          ),
          CapacityHeatmapService.getDemandData(
            this.branchCode,
            startDate,
            endDate,
          ),
        ]);

        this.utilisationGrid = CapacityHeatmapService.buildUtilisationGrid(
          capacityList,
          demandList,
          this.normalizedProcesses,
          this.columns,
        );
      } catch (err) {
        // Error fetching data — grid will remain empty
      } finally {
        this.showLoading(false);
        this.loading = false;
      }
    },

    getCellData(processCode, colKey) {
      if (
        this.utilisationGrid[processCode] &&
        this.utilisationGrid[processCode][colKey]
      ) {
        return this.utilisationGrid[processCode][colKey];
      }
      return null;
    },

    cellTierClass(cellData) {
      if (!cellData) return "tier-empty";
      const p = cellData.pct;
      if (p < 60) return "tier-low";
      if (p < 80) return "tier-ok";
      if (p < 90) return "tier-warn";
      if (p < 100) return "tier-high";
      return "tier-over";
    },

    avgTierClass(avg) {
      if (avg === null) return "avg-nodata";
      if (avg < 60) return "avg-low";
      if (avg < 80) return "avg-ok";
      if (avg < 90) return "avg-warn";
      if (avg < 100) return "avg-high";
      return "avg-over";
    },

    hasOverCapacity(colKey) {
      return this.normalizedProcesses.some((proc) => {
        const cell =
          this.utilisationGrid[proc.ProcessCode] &&
          this.utilisationGrid[proc.ProcessCode][colKey];
        return cell && cell.pct >= 100;
      });
    },

    toggleColHighlight(colKey) {
      this.highlightCol = this.highlightCol === colKey ? null : colKey;
      this.highlightRow = null;
    },

    toggleRowHighlight(processCode) {
      this.highlightRow =
        this.highlightRow === processCode ? null : processCode;
      this.highlightCol = null;
    },

    openDrill(proc, col) {
      const cellData = this.getCellData(proc.ProcessCode, col.key);
      if (!cellData) {
        this.clearDrill();
        return;
      }
      this.drillCell = {
        processCode: proc.ProcessCode,
        processName: proc.Description,
        colKey: col.key,
        colLabel: col.label,
        colSubLabel: col.subLabel,
        colDates: col.dates,
        ...cellData,
      };
    },

    clearDrill() {
      this.drillCell = null;
    },

    onViewInSchedule(payload) {
      this.clearDrill();
      this.$emit("navigate-to-schedule", payload);
    },

    showTooltip(e, proc, col) {
      const cellData = this.getCellData(proc.ProcessCode, col.key);
      if (!cellData) return;
      this.tooltip = {
        visible: true,
        x: e.clientX + 14,
        y: e.clientY - 10,
        content: {
          title: `${proc.Description} — ${col.label}`,
          capacity: cellData.capacityHours,
          demand: cellData.demandHours,
          pct: cellData.pct,
          remaining:
            cellData.pct < 100
              ? Math.round(
                  (cellData.capacityHours - cellData.demandHours) * 100,
                ) / 100
              : null,
          overBy:
            cellData.pct >= 100
              ? Math.round(
                  (cellData.demandHours - cellData.capacityHours) * 100,
                ) / 100
              : null,
          exportTonnes: cellData.exportTonnes || 0,
          otherTonnes: cellData.otherTonnes || 0,
          maintHours: cellData.maintHours || 0,
          workOrders: cellData.workOrders || [],
        },
      };
    },

    hideTooltip() {
      this.tooltip.visible = false;
    },
  },
};
</script>

<style scoped lang="scss">
.cap-heatmap-wrap {
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
  position: relative;
  background: #fff;

  .cap-loading {
    padding: 40px;
    color: #9ca3af;
    font-size: 13px;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .cap-grid-scroll {
    flex: 1;
    overflow: auto;
  }

  .cap-grid {
    display: grid;
    min-width: 100%;
  }

  /* Sticky positioning */
  .cap-sticky-tl {
    position: sticky;
    top: 0;
    left: 0;
    z-index: 4;
  }
  .cap-sticky-top {
    position: sticky !important;
    top: 0;
    z-index: 3;
  }
  .cap-sticky-left {
    position: sticky;
    left: 0;
    z-index: 2;
  }

  /* Corner */
  .cap-corner {
    background: #f9fafb;
    border-right: 1px solid #e5e7eb;
    border-bottom: 1px solid #e5e7eb;
    padding: 8px 12px;
    font-size: 11px;
    font-weight: 500;
    color: #9ca3af;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  /* Column headers */
  .cap-col-hdr {
    background: #f9fafb;
    border-right: 1px solid #e5e7eb;
    border-bottom: 1px solid #e5e7eb;
    padding: 6px 10px;
    text-align: center;
    cursor: pointer;
    position: relative;
    user-select: none;
    &:hover {
      background: #f3f4f6;
    }
  }
  .cap-col-label {
    font-size: 12px;
    font-weight: 500;
    color: #374151;
  }
  .cap-col-sub {
    font-size: 10px;
    color: #9ca3af;
    margin-top: 2px;
  }

  /* Over-capacity red dot */
  .cap-over-dot {
    position: absolute;
    top: 5px;
    right: 6px;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #dc2626;
  }

  /* Row headers */
  .cap-row-hdr {
    background: #fff;
    border-right: 1px solid #e5e7eb;
    border-bottom: 1px solid #e5e7eb;
    padding: 8px 12px;
    cursor: pointer;
    user-select: none;
    &:hover {
      background: #f9fafb;
    }
  }
  .cap-proc-name {
    font-size: 12px;
    font-weight: 500;
    color: #374151;
  }
  .cap-avg {
    font-size: 11px;
    margin-top: 3px;
  }

  /* Cells */
  .cap-cell {
    border-right: 1px solid #e5e7eb;
    border-bottom: 1px solid #e5e7eb;
    padding: 10px 8px;
    text-align: center;
    cursor: pointer;
    transition: filter 0.1s;
    &:hover {
      filter: brightness(0.95);
    }
  }
  .cap-pct {
    font-size: 13px;
    font-weight: 500;
    color: #374151;
  }
  .cap-hrs {
    font-size: 11px;
    color: #6b7280;
    margin-top: 2px;
  }
  .cap-nodata {
    font-size: 13px;
    color: #d1d5db;
  }

  /* Cell active border */
  .cap-cell-active {
    outline: 2px solid #2a95c5;
    outline-offset: -2px;
  }

  /* Highlight overlay */
  .cap-col-hl.cap-cell,
  .cap-row-hl.cap-cell {
    filter: brightness(0.93);
  }
  .cap-col-hl.cap-col-hdr,
  .cap-row-hl.cap-row-hdr {
    background: #e0f2fe;
  }

  /* Colour tiers */
  .tier-empty {
    background: #f9fafb;
  }
  .tier-low {
    background: #f0fdf4;
  }
  .tier-ok {
    background: #dcfce7;
  }
  .tier-warn {
    background: #fef3c7;
  }
  .tier-high {
    background: #fee2e2;
  }
  .tier-over {
    background: #fecaca;
  }

  /* Row avg colours */
  .avg-nodata {
    color: #9ca3af;
  }
  .avg-low {
    color: #16a34a;
  }
  .avg-ok {
    color: #15803d;
  }
  .avg-warn {
    color: #d97706;
  }
  .avg-high {
    color: #dc2626;
  }
  .avg-over {
    color: #991b1b;
    font-weight: 600;
  }

  /* Legend */
  .cap-legend {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-shrink: 0;
    padding: 6px 16px;
    border-top: 1px solid #e5e7eb;
    background: #fff;
    flex-wrap: wrap;
    gap: 8px;
  }
  .cap-legend-items {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }
  .leg-item {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 11px;
    color: #6b7280;
    white-space: nowrap;
  }
  .leg-swatch {
    display: inline-block;
    width: 14px;
    height: 14px;
    border-radius: 2px;
    flex-shrink: 0;
  }
  .cap-legend-right {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .leg-over-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #dc2626;
    flex-shrink: 0;
  }
  .leg-over-label {
    font-size: 11px;
    color: #6b7280;
    white-space: nowrap;
  }
  .leg-hint {
    font-size: 11px;
    color: #9ca3af;
    white-space: nowrap;
  }
}
</style>
